import { DateObject, type DateType, pad } from '@capsicum/common';

interface ReverseAuctionParams {
	decreasingPrice: number;
	endingPrice: number;
	price: number;
	startingDate: DateType;
	timeIntervalInSeconds: number;
}

type PriceChangeCallbackType = (price: number) => void;
type CountdownChangeCallbackType = (price: string) => void;

export class ReverseAuction {
	public nextBidCountdown = '--:--';

	private startTimeout: number | ReturnType<typeof setInterval> = 0;

	private timerTimeout: number | ReturnType<typeof setTimeout> = 0;

	private decreasingInterval: number | ReturnType<typeof setInterval> = 0;

	private decreasingTimeout: number | ReturnType<typeof setTimeout> = 0;

	private currentPrice = 0;

	private endingPrice = 0;

	public constructor(
		private options: ReverseAuctionParams,
		private onCountdownChange: CountdownChangeCallbackType,
		private onPriceChangeCallback: PriceChangeCallbackType
	) {
		if (typeof this.options.startingDate === 'string') {
			this.options.startingDate = new DateObject(this.options.startingDate);
		}

		this.currentPrice = options.price;
		this.endingPrice = options.endingPrice;
	}

	public start(skipTimeDiff = false) {
		if (Number(this.currentPrice) <= Number(this.endingPrice)) {
			this.nextBidCountdown = '';
			this.cancel();

			return;
		}

		const now = DateObject.now();
		const diffInMs = (this.options.startingDate as DateObject).getTime() - now;

		// Auction has not started yet
		if (diffInMs > 0) {
			// Start 1 secs later to prevent false starts
			clearTimeout(this.startTimeout as number);
			this.startTimeout = setTimeout(() => this.start(true), diffInMs);

			// Still display the timer
			this.nextBidCountdown = this.getCountDownString();

			return;
		}

		// Substract 6000 to simulate failed BE synchronisation
		const nextUpdateIn = this.getNextUpdateRemainingTimeInMs();
		const startUpdatePriceInterval = () => {
			clearInterval(this.decreasingInterval as number);
			this.decreasingInterval = setInterval(() => {
				this.decreasePrice();
			}, this.options.timeIntervalInSeconds * 1000);
		};

		// Decrease the price to keep track of it and cancel the service when price reaches endingPrice

		// if skipTimeDiff, it means the start methode has been called after a first "timeout" to delay the incoming start
		// so once we reach here, it means we're exactly at the good time ==> we can start the interval
		if (skipTimeDiff) {
			startUpdatePriceInterval();
		} else {
			// if there is no skipTimeDiff, it means the RA already started : we need first a timeout to reach the next update and then set the interval
			clearTimeout(this.decreasingTimeout as number);
			this.decreasingTimeout = setTimeout(() => {
				this.decreasePrice();
				startUpdatePriceInterval();
			}, nextUpdateIn);
		}

		if (!this.timerTimeout) {
			// We are watching nextBidCountdown in the component. No need to use callback.
			this.timerTimeout = setInterval(() => {
				this.nextBidCountdown = this.getCountDownString();
				this.onCountdownChange(this.nextBidCountdown);
			}, 1000);
		}
	}

	public decreasePrice(): void {
		this.currentPrice -= this.options.decreasingPrice;
		this.onPriceChangeCallback(this.currentPrice);

		if (this.currentPrice <= this.endingPrice) {
			this.nextBidCountdown = '';
			this.cancel();
		}
	}

	public getNextUpdateRemainingTimeInMs(): number {
		const now = new DateObject();
		const diffInMs = (this.options.startingDate?.valueOf() as number) - now.valueOf();
		const intervalDuration = this.options.timeIntervalInSeconds;

		// To display full timer, before auction starts
		if (diffInMs >= 0) {
			return intervalDuration * 1000;
		}

		// To get time for next bid down event and generate timer string
		return intervalDuration * 1000 - (Math.abs(diffInMs) % (intervalDuration * 1000));
	}

	public cancel(): void {
		clearTimeout(this.startTimeout as number);
		clearInterval(this.timerTimeout as number);
		clearInterval(this.decreasingInterval as number);
		clearTimeout(this.decreasingTimeout as number);
	}

	private getCountDownString(): string {
		const nextUpdateInSecs = this.getNextUpdateRemainingTimeInMs() / 1000;
		let minutes = Math.floor(nextUpdateInSecs / 60);
		let secs = pad(Math.round(nextUpdateInSecs % 60));

		if (secs === 60) {
			minutes += 1;
			secs = 0;
		}

		const minutesPadded = pad(minutes);

		return `${minutes < 0 ? '00' : minutesPadded}:${+secs <= 0 ? '00' : secs}`;
	}
}
